/**
 * Ledger state: the deterministic function of the accepted vertex set.
 *
 * Every node computes this independently from the same vertices in the
 * same topological order and must arrive at the same balances. Nothing
 * here may consult the clock, the network or randomness.
 */

import { fromHex, toHex, type Hex } from './bytes.ts';
import { addressFromPublicKey } from './keys.ts';
import { params, type ChainParams } from './params.ts';
import { halvingEpoch, lockedGrains, mintRewardForEpoch } from './mint.ts';
import {
  UniquenessIndex,
  identityId,
  type RegisteredIdentity,
  BANDS,
} from './identity.ts';
import type { GuardBody, MintBody, RotateBody, SignedVertex, TransferBody } from './tx.ts';
import { attestationChallenge, headerOf, senderAddress, signingPreimage, vouchPreimage } from './tx.ts';
import { pqVerify, verify } from './keys.ts';
import { verifyAssertion, type DeviceCredential } from './webauthn.ts';

export interface Account {
  address: string;
  /** Everything the account holds, locked portion included. */
  balance: bigint;
  /** Next expected nonce. Replay protection and per-sender ordering. */
  nonce: number;
  /** Set once, when this address minted its face allocation. */
  identityId?: string;
  /** Size of that airdrop. Fixed at mint; the unlock schedule divides it. */
  mintedGrains: bigint;
  /** Portion of the airdrop still locked. Never applies to received funds. */
  locked: bigint;
  /** Distinct new humans who joined naming this address as their referrer. */
  invites: number;
  /**
   * The platform authenticator registered when this wallet claimed.
   *
   * Once set, every spend from this account must carry a fresh assertion
   * from it. That is what stops a stolen or re-derived private key from
   * being enough - including a key derived from a photograph of the
   * owner's face.
   */
  device?: DeviceCredential;
  /** ML-DSA-44 public key, registered at the claim. */
  pq?: string;
  /**
   * Outgoing payments inside the guard window, newest last.
   *
   * The guard has to apply to the *total* a wallet has sent recently, not
   * to each payment on its own. A threshold applied per payment is
   * defeated by dividing: a coercer simply says "send it in slices", and
   * every slice lands unstoppably. Deterministic across nodes because a
   * sender's own vertices apply in nonce order everywhere.
   */
  recentOut?: { ts: number; amount: bigint }[];
  /** Ed25519 public key, kept so this account's vouches can be verified. */
  edPub?: string;
  firstSeen: number;
  txCount: number;
}

export interface ApplyResult {
  ok: boolean;
  reason?: string;
}

export interface LedgerSnapshot {
  accounts: [
    string,
    {
      balance: string; nonce: number; identityId?: string;
      mintedGrains: string; locked: string; invites: number;
      firstSeen: number; txCount: number;
    },
  ][];
  identities: RegisteredIdentity[];
  totalSupply: string;
  lockedSupply: string;
  appliedCount: number;
}

export class Ledger {
  private accounts = new Map<string, Account>();
  private uniqueness = new UniquenessIndex();
  private applied = new Set<string>();
  private supply = 0n;
  private mintsPerEpoch = new Map<number, number>();
  /**
   * referrer address -> identity ids of the humans who named it.
   *
   * Identity ids rather than a counter, because the count has to be a pure
   * function of the applied set: two nodes replaying the same mints in
   * different orders must reach the same number, and a set makes that true
   * by construction while also making a repeat impossible.
   */
  private referrals = new Map<string, Set<string>>();
  /** referrer -> epoch -> how many newcomers they have sponsored. */
  private vouchesByEpoch = new Map<string, Map<number, number>>();
  /**
   * Large transfers waiting out their guard window.
   *
   * The sender is debited the moment the transfer is accepted, so the
   * money cannot be spent twice, but the recipient is not credited until
   * the window closes. Until then the sender can take it back.
   */
  private guarded = new Map<string, { from: string; to: string; amount: bigint; matureAt: number }>();
  /** Announced device changes, keyed by the rotate vertex that asked. */
  private rotations = new Map<string, { account: string; device: DeviceCredential; matureAt: number }>();
  readonly chainParams: ChainParams;

  constructor(p: ChainParams = params()) {
    this.chainParams = p;
  }

  get totalSupply(): bigint {
    return this.supply;
  }
  get accountCount(): number {
    return this.accounts.size;
  }
  get humanCount(): number {
    return this.uniqueness.size;
  }
  get appliedCount(): number {
    return this.applied.size;
  }
  get index(): UniquenessIndex {
    return this.uniqueness;
  }
  /** Sum of every airdrop still waiting on invites. */
  get lockedSupply(): bigint {
    let total = 0n;
    for (const a of this.accounts.values()) total += a.locked;
    return total;
  }
  get circulatingSupply(): bigint {
    return this.supply - this.lockedSupply;
  }

  account(address: string): Account {
    let a = this.accounts.get(address);
    if (!a) {
      a = { address, balance: 0n, nonce: 0, mintedGrains: 0n, locked: 0n, invites: 0, firstSeen: 0, txCount: 0 };
      this.accounts.set(address, a);
    }
    return a;
  }

  balance(address: string): bigint {
    return this.accounts.get(address)?.balance ?? 0n;
  }

  /** Locked airdrop still waiting on invites. */
  locked(address: string): bigint {
    return this.accounts.get(address)?.locked ?? 0n;
  }

  /** What this address may actually spend right now. */
  available(address: string): bigint {
    const a = this.accounts.get(address);
    if (!a) return 0n;
    const v = a.balance - a.locked;
    return v > 0n ? v : 0n;
  }

  invites(address: string): number {
    return this.accounts.get(address)?.invites ?? 0;
  }

  nextNonce(address: string): number {
    return this.accounts.get(address)?.nonce ?? 0;
  }

  hasAccount(address: string): boolean {
    return this.accounts.has(address);
  }

  allAccounts(): Account[] {
    return [...this.accounts.values()];
  }

  richList(n = 50): Account[] {
    return this.allAccounts()
      .filter((a) => a.balance > 0n)
      .sort((a, b) => (b.balance > a.balance ? 1 : b.balance < a.balance ? -1 : 0))
      .slice(0, n);
  }

  mintsInEpoch(epoch: number): number {
    return this.mintsPerEpoch.get(epoch) ?? 0;
  }

  /**
   * Dry run. Consensus asks this before it will *prefer* a vertex, so a
   * transaction that cannot pay never wins a vote in the first place.
   */
  check(v: SignedVertex, opts: { ignoreNonce?: boolean } = {}): ApplyResult {
    if (this.applied.has(v.id)) return { ok: false, reason: 'already applied' };
    const from = senderAddress(v);
    const acct = this.accounts.get(from);
    const expectedNonce = acct?.nonce ?? 0;
    if (!opts.ignoreNonce && v.nonce !== expectedNonce) {
      return { ok: false, reason: `nonce out of order (expected ${expectedNonce}, got ${v.nonce})` };
    }

    if (v.type === 'rotate') {
      if (!acct?.device) return { ok: false, reason: 'this wallet has no device to change' };
      return { ok: true };
    }

    if (v.type === 'settle' || v.type === 'cancel') {
      const ref = (v.body as unknown as GuardBody).ref;
      const rot = this.rotations.get(ref);
      if (rot) {
        if (from !== rot.account) return { ok: false, reason: 'only the owner can act on a device change' };
        if (v.type === 'cancel') {
          // The veto, and the only thing that stops a stolen key changing
          // the device. It must come from the authenticator being replaced.
          const current = this.accounts.get(rot.account)?.device;
          if (!current) return { ok: false, reason: 'this wallet has no device to change' };
          if (v.ts >= rot.matureAt) return { ok: false, reason: 'the window to stop this device change has closed' };
          if (!v.attest) return { ok: false, reason: 'stopping a device change needs the current device' };
          const a = verifyAssertion(current, v.attest, attestationChallenge(headerOf(v), v.pow));
          if (!a.ok || !a.userVerified) return { ok: false, reason: 'the current device did not approve' };
        } else if (v.ts < rot.matureAt) {
          return { ok: false, reason: 'this device change has not matured yet' };
        }
        return { ok: true };
      }

      const g = this.guarded.get(ref);
      if (!g) return { ok: false, reason: 'that guarded transfer is not waiting' };
      if (v.type === 'cancel') {
        if (from !== g.from) return { ok: false, reason: 'only the sender can cancel a guarded transfer' };
        if (v.ts >= g.matureAt) return { ok: false, reason: 'the cancellation window has closed' };
      } else if (v.ts < g.matureAt) {
        return { ok: false, reason: 'this transfer has not matured yet' };
      }
      return { ok: true };
    }

    if (v.type === 'transfer') {
      const b = v.body as TransferBody;
      const amount = BigInt(b.amount);

      // A wallet bound to an authenticator can only be spent from with
      // that authenticator's consent, freshly given for this exact
      // vertex. Knowing the private key is not enough, which is the whole
      // point: the key is derivable from a face and the face is public.
      // The quantum half of the pair. Registered once, required forever.
      if (acct?.pq) {
        if (!v.pqsig || !pqVerify(acct.pq, signingPreimage(headerOf(v), v.pow), v.pqsig)) {
          return { ok: false, reason: 'post-quantum signature does not verify' };
        }
      }
      if (acct?.device) {
        if (!v.attest) return { ok: false, reason: 'this wallet requires its device to approve every payment' };
        const assertion = verifyAssertion(acct.device, v.attest, attestationChallenge(headerOf(v), v.pow));
        if (!assertion.ok) return { ok: false, reason: `device approval failed: ${assertion.reason}` };
        if (!assertion.userVerified) return { ok: false, reason: 'the device did not verify the owner' };
      }

      const available = this.available(from);
      if (available < amount) {
        const locked = acct?.locked ?? 0n;
        return {
          ok: false,
          reason: locked > 0n
            ? `insufficient unlocked balance - ${locked} grains are still locked until this wallet has invited ${this.chainParams.unlockInvites} new people`
            : 'insufficient balance',
        };
      }
      return { ok: true };
    }

    const b = v.body as MintBody;
    if (acct?.identityId) return { ok: false, reason: 'this wallet has already minted' };
    if (b.bands.length !== BANDS) return { ok: false, reason: 'wrong band count' };
    if (halvingEpoch(v.ts, this.chainParams) !== b.epoch) {
      return { ok: false, reason: 'declared halving epoch does not match the timestamp' };
    }
    if (BigInt(b.amount) !== mintRewardForEpoch(b.epoch, this.chainParams)) {
      return { ok: false, reason: 'mint amount does not match the schedule' };
    }

    // Past the bootstrap window a claim needs a sponsor: a registered
    // human, signing with both of their keys, spending one of a limited
    // number of vouches. See tx.ts for why this is the last line of
    // defence and what it does and does not achieve.
    if (this.needsVouch()) {
      const failure = this.checkVouch(v, b);
      if (failure) return { ok: false, reason: failure };
    }

    if (b.code) {
      const code = fromHex(b.code);
      const id = identityId(code);
      if (this.uniqueness.has(id)) return { ok: false, reason: 'this face is already registered' };
      const verdict = this.uniqueness.check(code, b.bands);
      if (verdict.verdict === 'duplicate') {
        return {
          ok: false,
          reason: `this face is already registered (identity ${verdict.nearestId}, ${(verdict.nearestFraction! * 100).toFixed(1)}% code distance)`,
        };
      }
      if (verdict.verdict === 'disputed') {
        return {
          ok: false,
          reason: `too close to an existing face to separate them (${(verdict.nearestFraction! * 100).toFixed(1)}% code distance) - re-scan in better light`,
        };
      }
    } else {
      // Private mode: no code published, so only the band index can speak.
      // Any band collision at all is fatal, because there is no second
      // stage available to clear it.
      for (const band of b.bands) {
        for (const other of this.uniqueness.all()) {
          if (other.bands.includes(band)) return { ok: false, reason: 'band collision with an existing face' };
        }
      }
    }
    return { ok: true };
  }

  /**
   * Would this payment, on top of what has already gone out recently,
   * cross the guard threshold?
   */
  isGuarded(address: string, amount: bigint, at: number): boolean {
    return amount + this.sentInWindow(address, at) >= this.chainParams.guardThresholdGrains;
  }

  /** Total sent from an address within the guard window ending at `at`. */
  sentInWindow(address: string, at: number): bigint {
    const recent = this.accounts.get(address)?.recentOut;
    if (!recent) return 0n;
    const from = at - this.chainParams.guardWindowMs;
    let total = 0n;
    for (const e of recent) if (e.ts > from && e.ts <= at) total += e.amount;
    return total;
  }

  /** What can still be sent freely before the guard engages. */
  freeAllowance(address: string, at: number): bigint {
    const left = this.chainParams.guardThresholdGrains - this.sentInWindow(address, at);
    return left > 0n ? left - 1n : 0n;
  }

  /** A guarded transfer awaiting release or cancellation. */
  guardedTransfer(ref: string): { from: string; to: string; amount: bigint; matureAt: number } | undefined {
    return this.guarded.get(ref);
  }

  /** Everything currently in escrow for an address, in either direction. */
  guardedFor(address: string): { ref: string; from: string; to: string; amount: bigint; matureAt: number }[] {
    const out = [];
    for (const [ref, g] of this.guarded) {
      if (g.from === address || g.to === address) out.push({ ref, ...g });
    }
    return out.sort((a, b) => a.matureAt - b.matureAt);
  }

  /** Commit an accepted vertex. Callers must apply in topological order. */
  apply(v: SignedVertex): ApplyResult {
    const pre = this.check(v);
    if (!pre.ok) return pre;

    const from = senderAddress(v);
    const sender = this.account(from);
    if (!sender.firstSeen) sender.firstSeen = v.ts;
    sender.nonce = v.nonce + 1;
    sender.txCount++;

    if (v.type === 'rotate') {
      this.rotations.set(v.id, {
        account: from,
        device: (v.body as unknown as RotateBody).device,
        matureAt: v.ts + this.chainParams.deviceChangeWindowMs,
      });
      this.applied.add(v.id);
      return { ok: true };
    }

    if (v.type === 'settle' || v.type === 'cancel') {
      const ref = (v.body as unknown as GuardBody).ref;
      const rot = this.rotations.get(ref);
      if (rot) {
        if (v.type === 'settle') this.account(rot.account).device = rot.device;
        this.rotations.delete(ref);
        this.applied.add(v.id);
        return { ok: true };
      }
      const g = this.guarded.get(ref)!;
      const beneficiary = this.account(v.type === 'settle' ? g.to : g.from);
      if (!beneficiary.firstSeen) beneficiary.firstSeen = v.ts;
      beneficiary.balance += g.amount;
      this.guarded.delete(ref);
      this.applied.add(v.id);
      return { ok: true };
    }

    if (v.type === 'transfer') {
      const b = v.body as TransferBody;
      const amount = BigInt(b.amount);
      sender.balance -= amount;
      const guarded = this.isGuarded(from, amount, v.ts);
      // Record the outgoing amount whether or not it was guarded: a
      // sequence of small payments has to accumulate towards the
      // threshold, or dividing defeats the whole mechanism.
      const recent = sender.recentOut ?? (sender.recentOut = []);
      recent.push({ ts: v.ts, amount });
      const cutoff = v.ts - this.chainParams.guardWindowMs;
      while (recent.length && recent[0].ts <= cutoff) recent.shift();

      if (guarded) {
        // Debited now, credited later. The gap is the window in which a
        // person who was made to send this can take it back.
        this.guarded.set(v.id, {
          from, to: b.to, amount,
          matureAt: v.ts + this.chainParams.guardWindowMs,
        });
      } else {
        const to = this.account(b.to);
        if (!to.firstSeen) to.firstSeen = v.ts;
        to.balance += amount;
      }
    } else {
      const b = v.body as MintBody;
      // Decide before registering: the moment the identity is added the
      // window may close, and check() and apply() must agree.
      const chargedVouch = this.needsVouch();
      const amount = BigInt(b.amount);
      sender.balance += amount;
      this.supply += amount;
      this.mintsPerEpoch.set(b.epoch, (this.mintsPerEpoch.get(b.epoch) ?? 0) + 1);

      const code = b.code ?? toHex(new Uint8Array(48));
      const id = b.code ? identityId(fromHex(b.code)) : `private:${v.id.slice(0, 32)}`;
      sender.identityId = id;
      this.uniqueness.register({
        id, code, bands: b.bands, address: from, mintedAt: v.ts,
        sketch: b.sketch, commit: b.commit, device: b.device,
      });

      // The airdrop arrives locked. Invites already credited to this
      // address count immediately - referring people before you claim is
      // the same work as referring them after.
      sender.device = b.device;
      sender.pq = b.pq;
      sender.edPub = v.from;
      sender.mintedGrains = amount;
      sender.invites = this.referrals.get(from)?.size ?? 0;
      this.recomputeLock(sender);

      // Credit the referrer. Deliberately not conditional on the referrer
      // being a registered human or even existing yet: the count is a set
      // of identity ids, so it is the same on every node no matter what
      // order the mints were applied in. Naming an address you control
      // gains nothing - unlocking needs distinct real faces, and each face
      // can only ever mint once.
      if (chargedVouch && b.referrer) {
        let epochs = this.vouchesByEpoch.get(b.referrer);
        if (!epochs) this.vouchesByEpoch.set(b.referrer, (epochs = new Map()));
        epochs.set(b.epoch, (epochs.get(b.epoch) ?? 0) + 1);
      }

      if (b.referrer && b.referrer !== from) {
        let set = this.referrals.get(b.referrer);
        if (!set) this.referrals.set(b.referrer, (set = new Set()));
        if (!set.has(id)) {
          set.add(id);
          const referrer = this.account(b.referrer);
          if (!referrer.firstSeen) referrer.firstSeen = v.ts;
          referrer.invites = set.size;
          this.recomputeLock(referrer);
        }
      }
    }

    this.applied.add(v.id);
    return { ok: true };
  }

  /**
   * Does a claim need a sponsor yet?
   *
   * The first `openClaims` people cannot be vouched for, because there is
   * nobody registered to vouch. After that the door is not shut, but you
   * have to be let in by someone who is already inside and who spends part
   * of a limited budget to do it.
   */
  needsVouch(): boolean {
    return this.uniqueness.size >= this.chainParams.openClaims;
  }

  /** Returns a reason string when the vouch is unacceptable, else null. */
  private checkVouch(v: SignedVertex, b: MintBody): string | null {
    if (!b.referrer || !b.vouch) {
      return 'a claim needs a voucher: someone already registered has to sponsor you';
    }
    const sponsor = this.accounts.get(b.referrer);
    if (!sponsor?.identityId || !sponsor.pq || !sponsor.edPub) {
      return 'the voucher is not a registered human';
    }
    const claimId = b.code ? identityId(fromHex(b.code)) : `private:${v.id.slice(0, 32)}`;
    const preimage = vouchPreimage(claimId, b.referrer);
    // Both signatures, for the same reason accounts carry both: a quantum
    // adversary must not be able to manufacture sponsors.
    if (!verify(sponsor.edPub, preimage, b.vouch.ed)) return 'the vouch signature does not verify';
    if (!pqVerify(sponsor.pq, preimage, b.vouch.pq)) return 'the post-quantum vouch signature does not verify';

    const used = this.vouchesByEpoch.get(b.referrer)?.get(b.epoch) ?? 0;
    if (used >= this.chainParams.vouchesPerEpoch) {
      return `that voucher has already sponsored ${used} people this epoch`;
    }
    return null;
  }

  /** Device changes announced for an address and not yet resolved. */
  pendingRotations(address: string): { ref: string; device: DeviceCredential; matureAt: number }[] {
    const out = [];
    for (const [ref, r] of this.rotations) {
      if (r.account === address) out.push({ ref, device: r.device, matureAt: r.matureAt });
    }
    return out.sort((a, b) => a.matureAt - b.matureAt);
  }

  /** Vouches this address has spent in an epoch. */
  vouchesUsed(address: string, epoch: number): number {
    return this.vouchesByEpoch.get(address)?.get(epoch) ?? 0;
  }

  /** Whether claims still need no sponsor. */
  get inBootstrapWindow(): boolean {
    return this.uniqueness.size < this.chainParams.openClaims;
  }

  /** Locked amount is a pure function of the airdrop and the invite count. */
  private recomputeLock(account: Account): void {
    account.locked = lockedGrains(account.mintedGrains, account.invites, this.chainParams);
  }

  /**
   * Find the wallet a face already owns.
   *
   * This is the lookup that makes one-face-one-wallet real rather than
   * merely stated: a returning person is routed to recovery, and a second
   * wallet for a face that already has one is never created in the first
   * place.
   */
  findRegisteredFace(code: Uint8Array, bands: Hex[]): RegisteredIdentity | undefined {
    const verdict = this.uniqueness.check(code, bands);
    if (verdict.verdict !== 'duplicate' || !verdict.nearestId) return undefined;
    return this.uniqueness.get(verdict.nearestId);
  }

  /** Who this address has brought in, for the wallet's invite panel. */
  referralIds(address: string): string[] {
    return [...(this.referrals.get(address) ?? [])];
  }

  /** The same, resolved to the wallets those people minted to. */
  invitedWallets(address: string): { id: string; address: string; mintedAt: number }[] {
    const out: { id: string; address: string; mintedAt: number }[] = [];
    for (const id of this.referralIds(address)) {
      const reg = this.uniqueness.get(id);
      if (reg) out.push({ id, address: reg.address, mintedAt: reg.mintedAt });
    }
    return out.sort((a, b) => a.mintedAt - b.mintedAt);
  }

  wasApplied(id: string): boolean {
    return this.applied.has(id);
  }

  snapshot(): LedgerSnapshot {
    return {
      accounts: [...this.accounts.entries()].map(([k, a]) => [
        k,
        {
          balance: a.balance.toString(), nonce: a.nonce, identityId: a.identityId,
          mintedGrains: a.mintedGrains.toString(), locked: a.locked.toString(), invites: a.invites,
          firstSeen: a.firstSeen, txCount: a.txCount,
        },
      ]),
      identities: this.uniqueness.snapshot(),
      totalSupply: this.supply.toString(),
      lockedSupply: this.lockedSupply.toString(),
      appliedCount: this.applied.size,
    };
  }

  /** Address an issuer key will credit. Exposed for wallet-side previews. */
  static addressOf(publicKeyHex: Hex): string {
    return addressFromPublicKey(fromHex(publicKeyHex));
  }
}
